import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../../CSS/EmployeeMaster.css";
import api from "../../api.js";

/* ============================================================
   ILMS · Employee Master (Configure module)
   Saami Tradestar Logistics Pvt Ltd
   Design system: Plus Jakarta Sans / JetBrains Mono,
   blue #1e6be6, teal #0e9f8c, navy #0f1e40, amber sidebar band.
   ============================================================ */

/* ---------- Reference data ----------
   Designation, Department, Branch/Posting, Reporting manager and
   Employment type are no longer hardcoded here — they're fetched from
   the /dropdown API (Desig / Dept / plc / Rep_Mger / EmpType modes)
   inside EmployeeMaster(). See the useEffect below. */
const DEPT_COLORS = ["#1e6be6", "#0e9f8c", "#ff8a3c", "#7c5cff", "#d9435f", "#0aa5c9", "#8a6d3b"];
const deptColor = (n) => {
  if (!n) return "#7c879c";
  let s = 0; for (const c of n) s += c.charCodeAt(0);
  return DEPT_COLORS[s % DEPT_COLORS.length];
};
const SHIFTS = ["General (9–6)", "Morning (6–2)", "Evening (2–10)", "Night (10–6)"];
const STATUSES = ["Active", "On Leave", "Inactive"];
const BLOOD = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];
const AV = ["#1e6be6","#0e9f8c","#ff8a3c","#7c5cff","#d9435f","#0aa5c9","#3b5bdb","#e8590c"];

/* ---------- Helpers ---------- */
// const initials = (n) => { const p = n.trim().split(/\s+/); return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase(); };
// const avColor = (n) => { let s = 0; for (const c of n) s += c.charCodeAt(0); return AV[s % AV.length]; };
const initials = (n) => {
  const text = String(n || "").trim();
  const p = text.split(/\s+/);

  return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase();
};

const avColor = (n) => { const text = String(n || ""); let s = 0; for (const c of text) { s += c.charCodeAt(0); } return AV[s % AV.length];};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const badgeCls = (s) => (s === "Active" ? "ok" : s === "On Leave" ? "warn" : "off");
const toDateInput = (d) => (d ? String(d).slice(0, 10) : "");

/* ---------- id <-> display-name helpers for the /dropdown master lists ---------- */
const mapIdName = (rows, idKey, nameKey) => (rows || []).map((r) => ({ id: r[idKey], name: r[nameKey] }));
const nameById = (list, id) => list.find((x) => x.id === id)?.name || "";
const idByName = (list, name) => list.find((x) => x.name === name)?.id ?? null;

/* Turns a raw Employee row from the API into the shape the grid/filters use.
   designation/department/branch/type/manager are resolved to display names
   using the master lists so the table doesn't need to know about ids at all;
   `raw` keeps the original record (with ids) around for editing. */
function mapEmployeeRow(e, lists) {
  return {
    id: e.emp_Code,
    code: e.employee_Code || `EMP${String(e.emp_Code).padStart(2, "0")}`,
    name: e.emp_Name,
    designation: nameById(lists.designations, e.designation_id),
    department: nameById(lists.departments, e.dept_Id),
    branch: nameById(lists.branches, e.branch_id),
    mobile: e.emp_Mobile || "",
    email: e.emp_Email || "",
    type: nameById(lists.empTypes, e.emp_Type_id),
    doj: e.emp_DOJ,
    status: e.emp_Status || "Active",
    manager: e.reporting_Manager_Id ? nameById(lists.managers, e.reporting_Manager_Id) : "—",
    raw: e,
  };
}

/* ============================================================
   Combo — system-wide auto-commit standard
   type-to-filter · ↑/↓ nav · Enter/click commit · exact-match
   on close · body-appended panel via createPortal.
   ============================================================ */
function Combo({ options, value, onChange, placeholder = "Select…", minWidth = 170 }) {
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value);
  const [typed, setTyped] = useState(false);
  const [active, setActive] = useState(0);
  const [rect, setRect] = useState({ left: 0, top: 0, width: 0, drop: true });

  useEffect(() => { setText(value); }, [value]);

  const filtered = useMemo(() => {
    const t = typed ? text.trim().toLowerCase() : "";
    return options.filter((o) => o.toLowerCase().includes(t));
  }, [options, text, typed]);

  const position = () => {
    const el = inputRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    const drop = !(below < 240 && r.top > below);
    setRect({ left: r.left, top: drop ? r.bottom + 5 : r.top - 5, width: r.width, drop });
  };
  useLayoutEffect(() => { if (open) position(); }, [open, filtered.length]);
  useEffect(() => {
    if (!open) return;
    const h = () => position();
    window.addEventListener("scroll", h, true);
    window.addEventListener("resize", h);
    return () => { window.removeEventListener("scroll", h, true); window.removeEventListener("resize", h); };
  }, [open]);

  const doOpen = () => { setTyped(false); setActive(Math.max(0, options.findIndex((o) => o === value))); setOpen(true); };
  const commit = (v) => { onChange(v); setText(v); };
  const close = () => {
    setOpen(false);
    // exact-match on close: keep committed value unless free text matches an option
    const exact = options.find((o) => o.toLowerCase() === text.trim().toLowerCase());
    if (exact) commit(exact); else setText(value);
    setTyped(false);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); if (!open) doOpen(); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); const v = filtered[active]; if (v) { commit(v); setOpen(false); inputRef.current?.blur(); } }
    else if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); }
  };

  return (
    <div className={"ilms-combo" + (open ? " open" : "")} ref={wrapRef} style={{ minWidth }}>
      <input
        ref={inputRef} className="ilms-combo-input" placeholder={placeholder} value={text}
        onFocus={doOpen}
        onChange={(e) => { setText(e.target.value); setTyped(true); setActive(0); if (!open) setOpen(true); }}
        onKeyDown={onKey}
        onBlur={() => setTimeout(close, 120)}
      />
      <span className="ilms-combo-caret">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M6 9l6 6 6-6" /></svg>
      </span>
      {open && createPortal(
        <div className="ilms-combo-panel" style={{ left: rect.left, width: rect.width, ...(rect.drop ? { top: rect.top } : { bottom: window.innerHeight - rect.top }) }}>
          {filtered.length === 0 ? (
            <div className="ilms-combo-empty">No matches</div>
          ) : filtered.map((o, i) => (
            <div
              key={o}
              className={"ilms-combo-opt" + (i === active ? " active" : "") + (o === value ? " sel" : "")}
              onMouseDown={(e) => { e.preventDefault(); commit(o); setOpen(false); }}
              onMouseEnter={() => setActive(i)}
            >
              {o}{o === value && <small>✓</small>}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

/* ---------- Segmented control ---------- */
function Segmented({ options, value, onChange }) {
  return (
    <div className="ilms-seg">
      {options.map((o) => (
        <button key={o} className={value === o ? "on" : ""} onClick={() => onChange(o)}>{o}</button>
      ))}
    </div>
  );
}

/* ============================================================
   Drawer (Add / Edit)
   ============================================================ */
const BLANK = () => ({
  code: "", name: "", mobile: "", email: "", gender: "Male", dob: "", addr: "", emergency: "",
  designation: "", department: "", branch: "", manager: "— None —", type: "",
  doj: "", shift: "General (9–6)", status: "Active", ctc: "", blood: "",
  pan: "", aadhaar: "", uan: "", esic: "", acc: "", ifsc: "", bank: "",
});

/* Defined at module scope (not inside Drawer) so React keeps treating it as
   the SAME component across re-renders. If this were declared inside Drawer,
   every keystroke (which updates `draft` and re-renders Drawer) would create
   a brand-new Fld function, React would remount the <input>, and the cursor
   would jump out after every character. */
function Fld({ draft, onField, k, label, req, mono, ph, up }) {
  return (
    <label className="ilms-fl">
      <span className="lt">{label}{req && <span className="req">*</span>}</span>
      <input className={"ilms-in" + (mono ? " mono" : "")} placeholder={ph} value={draft[k] || ""}
        style={up ? { textTransform: "uppercase" } : undefined}
        onChange={(e) => onField(k, e.target.value)} />
    </label>
  );
}

function Drawer({ open, editing, viewOnly, draft, designations, departments, branches, empTypes, managers, onField, onCombo, onClose, onSave }) {
  const [tab, setTab] = useState("personal");
  useEffect(() => { if (open) setTab("personal"); }, [open, editing]);

  return (
    <>
      <div className={"ilms-scrim" + (open ? " show" : "")} onClick={onClose} />
      <aside className={"ilms-drawer" + (open ? " show" : "")} aria-hidden={!open}>
        <div className="dhead">
          <div className="dav">{editing ? initials(draft.name || "") : "+"}</div>
          <div>
            <h3>{viewOnly ? "View Employee" : editing ? "Edit Employee" : "Add Employee"}</h3>
            <p>{editing ? `${draft.code} · ${draft.name}` : "Create a new staff record"}</p>
          </div>
          <button className="close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="dbody">
          <div className="ilms-sectab">
            {["personal", "employment", "statutory"].map((t) => (
              <button key={t} className={tab === t ? "on" : ""} onClick={() => setTab(t)}>
                {t[0].toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          <fieldset disabled={viewOnly} style={{ border: 0, margin: 0, padding: 0 }}>
          {tab === "personal" && (
            <div className="ilms-fgrid">
              <Fld draft={draft} onField={onField} k="name" label="Full name" req ph="e.g. Rajesh Sharma" />
              <label className="ilms-fl"><span className="lt">Employee code</span>
                <input className="ilms-in mono" value={draft.code || "Auto-generated on save"} disabled />
                <span className="hint">Assigned automatically by the server (EMP01, EMP02, …).</span>
              </label>
              <Fld draft={draft} onField={onField} k="mobile" label="Mobile" req mono ph="+91 90000 00000" />
              <Fld draft={draft} onField={onField} k="email" label="Email" ph="name@saamitradestar.com" />
              <label className="ilms-fl"><span className="lt">Gender</span>
                <Segmented options={["Male", "Female", "Other"]} value={draft.gender} onChange={(v) => onCombo("gender", v)} />
              </label>
              <label className="ilms-fl"><span className="lt">Date of birth</span>
                <input className="ilms-in" type="date" value={draft.dob} onChange={(e) => onField("dob", e.target.value)} />
              </label>
              <label className="ilms-fl full"><span className="lt">Address</span>
                <textarea className="ilms-in" placeholder="Street, area, city, PIN" value={draft.addr} onChange={(e) => onField("addr", e.target.value)} />
              </label>
              <Fld draft={draft} onField={onField} k="emergency" label="Emergency contact" mono ph="+91 90000 00000" />
              <label className="ilms-fl"><span className="lt">Blood group</span>
                <Combo options={BLOOD} value={draft.blood} onChange={(v) => onCombo("blood", v)} placeholder="Select group" />
              </label>
            </div>
          )}

          {tab === "employment" && (
            <div className="ilms-fgrid">
              <label className="ilms-fl"><span className="lt">Designation<span className="req">*</span></span>
                <Combo options={designations.map((d) => d.name)} value={draft.designation} onChange={(v) => onCombo("designation", v)} placeholder="Select designation" />
              </label>
              <label className="ilms-fl"><span className="lt">Department<span className="req">*</span></span>
                <Combo options={departments.map((d) => d.name)} value={draft.department} onChange={(v) => onCombo("department", v)} placeholder="Select department" />
              </label>
              <label className="ilms-fl"><span className="lt">Branch / Posting<span className="req">*</span></span>
                <Combo options={branches.map((b) => b.name)} value={draft.branch} onChange={(v) => onCombo("branch", v)} placeholder="Select branch" />
              </label>
              <label className="ilms-fl"><span className="lt">Reporting manager</span>
                <Combo options={["— None —", ...managers.map((m) => m.name)]} value={draft.manager} onChange={(v) => onCombo("manager", v)} placeholder="Select manager" />
              </label>
              <label className="ilms-fl"><span className="lt">Employment type</span>
                <Combo options={empTypes.map((t) => t.name)} value={draft.type} onChange={(v) => onCombo("type", v)} placeholder="Select type" />
              </label>
              <label className="ilms-fl"><span className="lt">Date of joining<span className="req">*</span></span>
                <input className="ilms-in" type="date" value={draft.doj} onChange={(e) => onField("doj", e.target.value)} />
              </label>
              <label className="ilms-fl"><span className="lt">Shift</span>
                <Combo options={SHIFTS} value={draft.shift} onChange={(v) => onCombo("shift", v)} placeholder="Select shift" />
              </label>
              <label className="ilms-fl"><span className="lt">Status</span>
                <Combo options={STATUSES} value={draft.status} onChange={(v) => onCombo("status", v)} placeholder="Select status" />
              </label>
              <label className="ilms-fl full"><span className="lt">Monthly CTC (₹)</span>
                <input className="ilms-in mono" placeholder="0" value={draft.ctc} onChange={(e) => onField("ctc", e.target.value)} />
                <span className="hint">Visible to payroll & admin roles only.</span>
              </label>
            </div>
          )}

          {tab === "statutory" && (
            <div className="ilms-fgrid">
              <div className="grp-title">Identity</div>
              <Fld draft={draft} onField={onField} k="pan" label="PAN" mono ph="ABCDE1234F" up />
              <Fld draft={draft} onField={onField} k="aadhaar" label="Aadhaar" mono ph="0000 0000 0000" />
              <div className="grp-title">Statutory</div>
              <Fld draft={draft} onField={onField} k="uan" label="PF / UAN number" mono ph="000000000000" />
              <Fld draft={draft} onField={onField} k="esic" label="ESIC number" mono ph="00000000000000000" />
              <div className="grp-title">Payroll bank</div>
              <Fld draft={draft} onField={onField} k="acc" label="Account number" mono ph="000000000000" />
              <Fld draft={draft} onField={onField} k="ifsc" label="IFSC code" mono ph="HDFC0000000" up />
              <label className="ilms-fl full"><span className="lt">Bank name & branch</span>
                <input className="ilms-in" placeholder="e.g. HDFC Bank, Bhiwandi" value={draft.bank} onChange={(e) => onField("bank", e.target.value)} />
              </label>
            </div>
          )}
          </fieldset>
        </div>

        <div className="dfoot">
          {viewOnly ? (
            <>
              <span className="grow hint2">Read-only — this record can't be changed from here.</span>
              <button className="ilms-btn primary" onClick={onClose}>Close</button>
            </>
          ) : (
            <>
              <span className="grow hint2">Fields marked <b>*</b> are required.</span>
              <button className="ilms-btn" onClick={onClose}>Cancel</button>
              <button className="ilms-btn primary" onClick={onSave}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M20 6L9 17l-5-5" /></svg>
                Save Employee
              </button>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

/* ============================================================
   Main screen
   ============================================================ */
export default function EmployeeMaster() {
  const [emp, setEmp] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fDept, setFDept] = useState("All departments");
  const [fBranch, setFBranch] = useState("All branches");
  const [fStatus, setFStatus] = useState("All statuses");
  const [sort, setSort] = useState("name");
  const [dir, setDir] = useState(1);
  const [page, setPage] = useState(1);
  const per = 8;

  const [drawer, setDrawer] = useState(false);
  const [editing, setEditing] = useState(null); // holds the Emp_Code (int) being edited, or null when adding
  const [viewOnly, setViewOnly] = useState(false); // true when opened from the View icon — form is read-only
  const [draft, setDraft] = useState(BLANK());
  const [toast, setToast] = useState(null);
  const toastT = useRef(undefined);

  const notify = (msg, warn = false) => {
    setToast({ msg, warn });
    window.clearTimeout(toastT.current);
    toastT.current = window.setTimeout(() => setToast(null), 2400);
  };

  /* ---------- Dropdown master data (from /dropdown API) ----------
     Kept as {id, name} pairs — the id is what gets sent to /api/Employee,
     the name is what the Combo boxes and the grid display. */
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [empTypes, setEmpTypes] = useState([]);
  const [managers, setManagers] = useState([]);

  // Reloads the employee grid from /api/Employee using whatever master
  // lists are currently in state (call this after add/edit, or on demand).
  const loadEmployees = async (lists) => {
    const l = lists || { designations, departments, branches, empTypes, managers };
    try {
      const res = await api.get("/api/Employee");
      setEmp(res.data.map((e) => mapEmployeeRow(e, l)));
    } catch (err) {
      console.error("Failed to load employees", err);
      notify("Could not load employees from server", true);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [desigRes, deptRes, branchRes, typeRes, mgrRes] = await Promise.all([
          api.get("/dropdown?mode=Desig"),
          api.get("/dropdown?mode=Dept"),
          api.get("/dropdown?mode=plc"),
          api.get("/dropdown?mode=EmpType"),
          api.get("/dropdown?mode=Rep_Mger"),
        ]);
        if (!alive) return;
        // Column names below match what the SQL SELECT in DropdownController returns.
        const desigList = mapIdName(desigRes.data, "Desg_id", "Designation");
        const deptList = mapIdName(deptRes.data, "Dept_Id", "Department_Name");
        const branchList = mapIdName(branchRes.data, "place_id", "place_Name");
        const typeList = mapIdName(typeRes.data, "Emp_Type_id", "Employee_Type");
        const mgrList = mapIdName(mgrRes.data, "Emp_Code", "Emp_Name");
        setDesignations(desigList);
        setDepartments(deptList);
        setBranches(branchList);
        setEmpTypes(typeList);
        setManagers(mgrList);

        await loadEmployees({ designations: desigList, departments: deptList, branches: branchList, empTypes: typeList, managers: mgrList });
      } catch (err) {
        console.error("Failed to load master data", err);
        notify("Could not load data from server", true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const rows = emp.filter((e) => {
      if (fDept !== "All departments" && e.department !== fDept) return false;
      if (fBranch !== "All branches" && e.branch !== fBranch) return false;
      if (fStatus !== "All statuses" && e.status !== fStatus) return false;
      if (q) {
        const t = q.toLowerCase();
        if (![e.name, e.code, e.email, e.designation].some((x) => x.toLowerCase().includes(t))) return false;
      }
      return true;
    });
    rows.sort((a, b) => {
      let x = a[sort], y = b[sort];
      if (sort === "doj") { x = new Date(x).getTime(); y = new Date(y).getTime(); }
      return (x > y ? 1 : x < y ? -1 : 0) * dir;
    });
    return rows;
  }, [emp, q, fDept, fBranch, fStatus, sort, dir]);

  const pages = Math.max(1, Math.ceil(filtered.length / per));
  const curPage = Math.min(page, pages);
  const pageRows = filtered.slice((curPage - 1) * per, (curPage - 1) * per + per);

  const exportCsv = () => {
    if (filtered.length === 0) { notify("Nothing to export — adjust or clear filters", true); return; }

    const headers = ["Employee Code", "Name", "Designation", "Department", "Branch", "Mobile", "Email", "Employment Type", "Date of Joining", "Status", "Reporting Manager"];
    const escape = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = filtered.map((e) => [
      e.code, e.name, e.designation, e.department, e.branch,
      e.mobile, e.email, e.type, fmtDate(e.doj), e.status, e.manager,
    ].map(escape).join(","));

    const csv = [headers.map(escape).join(","), ...rows].join("\r\n");
    // Leading BOM so Excel opens the UTF-8 file (₹, names with accents, etc.) correctly.
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `employees_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const kpi = useMemo(() => ({
    total: emp.length,
    active: emp.filter((e) => e.status === "Active").length,
    leave: emp.filter((e) => e.status === "On Leave").length,
    depts: new Set(emp.map((e) => e.department)).size,
  }), [emp]);

  const clearFilters = () => { setQ(""); setFDept("All departments"); setFBranch("All branches"); setFStatus("All statuses"); setPage(1); };
  const hasFilters = q || fDept !== "All departments" || fBranch !== "All branches" || fStatus !== "All statuses";
  const toggleSort = (s) => { if (sort === s) setDir((d) => -d); else { setSort(s); setDir(1); } };

  const openAdd = () => {
    setEditing(null);
    setViewOnly(false);
    setDraft(BLANK());
    setDrawer(true);
  };
  const openEdit = (id, readOnly = false) => {
    const row = emp.find((x) => x.id === id); if (!row) return;
    const e = row.raw || {};
    setEditing(id);
    setViewOnly(readOnly);
    setDraft({
      // NOTE: `e` is the raw row exactly as the API returned it (camelCase,
      // same convention mapEmployeeRow uses above) — NOT the C# PascalCase
      // property names. Using e.g. e.Emp_Name here always came back
      // undefined and silently reset every field to blank.
      ...BLANK(), code: row.code, name: e.emp_Name || "",
      mobile: e.emp_Mobile || "", email: e.emp_Email || "",
      gender: e.gender === "F" ? "Female" : e.gender === "O" ? "Other" : "Male",
      dob: toDateInput(e.emp_DOB), addr: e.emp_Address || "", emergency: e.emergency_Contact || "",
      blood: e.blood_Group || "",
      designation: nameById(designations, e.designation_id),
      department: nameById(departments, e.dept_Id),
      branch: nameById(branches, e.branch_id),
      manager: e.reporting_Manager_Id ? nameById(managers, e.reporting_Manager_Id) : "— None —",
      type: nameById(empTypes, e.emp_Type_id),
      doj: toDateInput(e.emp_DOJ), shift: e.shift || "General (9–6)", status: e.emp_Status || "Active",
      ctc: e.emp_CTC ?? "",
      pan: e.pAN || "", aadhaar: e.aadhaar || "", uan: e.uAN || "", esic: e.eSIC || "",
      acc: e.bank_Account_No || "", ifsc: e.iFSC_Code || "", bank: e.bank_Name || "",
    });
    setDrawer(true);
  };
  const setF = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (!draft.name.trim()) { notify("Name is required", true); return; }
    if (!draft.designation || !draft.department || !draft.branch) { notify("Designation, department and branch are required", true); return; }

    const payload = {
      Emp_Name: draft.name.trim(),
      Emp_Mobile: draft.mobile.trim(),
      Emp_Email: draft.email.trim(),
      Gender: draft.gender === "Female" ? "F" : draft.gender === "Other" ? "O" : "M",
      Emp_DOB: draft.dob || null,
      Emp_Address: draft.addr,
      Emergency_Contact: draft.emergency,
      Blood_Group: draft.blood,
      Designation_id: idByName(designations, draft.designation),
      Dept_Id: idByName(departments, draft.department),
      Branch_id: idByName(branches, draft.branch),
      Reporting_Manager_Id: draft.manager && draft.manager !== "— None —" ? idByName(managers, draft.manager) : null,
      Emp_Type_id: idByName(empTypes, draft.type),
      Shift: draft.shift,
      Emp_Status: draft.status || "Active",
      Emp_DOJ: draft.doj || new Date().toISOString().slice(0, 10),
      Emp_CTC: draft.ctc === "" ? 0 : draft.ctc,
      PAN: draft.pan, Aadhaar: draft.aadhaar, UAN: draft.uan, ESIC: draft.esic,
      Bank_Account_No: draft.acc, IFSC_Code: draft.ifsc, Bank_Name: draft.bank,
      // Same pattern as JobSaveRequest.CreatedBy in JobMaster — sent from the
      // client instead of relying on server session. Swap the line below for
      // however you actually read the logged-in user's id in this app
      // (AuthContext, localStorage, a decoded token, etc.).
      Created_By: Number(localStorage.getItem("user_id")) || null,
    };

    try {
      if (editing) {
        await api.post(`/api/Employee/Update/${editing}`, { Emp_Code: editing, ...payload });
        notify(payload.Emp_Name + " updated");
      } else {
        await api.post("/api/Employee", payload);
        notify(payload.Emp_Name + " added");
      }
      setDrawer(false);
      await loadEmployees();
    } catch (err) {
      console.error("Save failed", err);
      notify("Save failed — please check the required fields", true);
    }
  };

  const toggleStatus = async (id) => {
    try {
      const res = await api.post(`/api/Employee/ToggleStatus/${id}`);
      const updated = res.data;
      setEmp((list) => list.map((e) => (e.id === id ? mapEmployeeRow(updated, { designations, departments, branches, empTypes, managers }) : e)));
      notify(`${updated.emp_Name} marked ${updated.emp_Status}`);
    } catch (err) {
      console.error("Status toggle failed", err);
      notify("Could not update status", true);
    }
  };

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") setDrawer(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const start = (curPage - 1) * per;
  const pagerBtns = [];
  for (let i = 1; i <= pages; i++) {
    if (pages > 7 && i > 2 && i < pages - 1 && Math.abs(i - curPage) > 1) {
      if (i === 3 || i === pages - 2) pagerBtns.push("…");
      continue;
    }
    pagerBtns.push(i);
  }

  return (
    <div className="ilms-root">
      {/* Main (sidebar removed — the global SaamiTradestarLogistics app shell
            already renders the sidebar; this component only owns its own
            content area now) */}
        <div className="ilms-main">
          <div className="topbar">
            <div className="crumb">
              <span>Configure</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 6l6 6-6 6" /></svg>
              <b>Employee Master</b>
            </div>
            <div className="spacer" />
            <div className="top-search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
              <input placeholder="Search anything…" />
            </div>
            <button className="icon-btn"><span className="dot" /><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 01-3.4 0" /></svg></button>
          </div>

          <div className="content">
            <div className="phead">
              <div>
                <h2>Employee Master</h2>
                <p>Manage staff records, roles and postings across all branches.</p>
              </div>
              <div className="acts">
                <button className="ilms-btn" onClick={exportCsv}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 15V3M8 11l4 4 4-4" /><path d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" /></svg>Export</button>
                <button className="ilms-btn primary" onClick={openAdd}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M12 5v14M5 12h14" /></svg>Add Employee</button>
              </div>
            </div>

            {/* KPIs */}
            <div className="kpis">
              <Kpi tone="b" label="Total Employees" value={kpi.total} sub="Across 5 branches" icon="team" />
              <Kpi tone="t" label="Active" value={kpi.active} sub="Currently working" icon="check" />
              <Kpi tone="a" label="On Leave" value={kpi.leave} sub="Approved absence" icon="clock" />
              <Kpi tone="g" label="Departments" value={kpi.depts} sub="Functional units" icon="dept" />
            </div>

            {/* Table */}
            <div className="ilms-card">
              <div className="toolbar">
                <div className="search-in">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
                  <input placeholder="Search name, code, email…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
                </div>
                <Combo options={["All departments", ...departments.map((d) => d.name)]} value={fDept} onChange={(v) => { setFDept(v); setPage(1); }} placeholder="Department" />
                <Combo options={["All branches", ...branches.map((b) => b.name)]} value={fBranch} onChange={(v) => { setFBranch(v); setPage(1); }} placeholder="Branch" />
                <Combo options={["All statuses", ...STATUSES]} value={fStatus} onChange={(v) => { setFStatus(v); setPage(1); }} placeholder="Status" />
                <div className="grow" />
                {hasFilters && (
                  <button className="chip on" onClick={clearFilters}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12" /></svg>Clear filters
                  </button>
                )}
              </div>

              <div className="tbl-wrap">
                <table>
                  <thead>
                    <tr>
                      <Th label="Employee" k="name" sort={sort} dir={dir} on={toggleSort} w={230} />
                      <Th label="Designation" k="designation" sort={sort} dir={dir} on={toggleSort} />
                      <Th label="Department" k="department" sort={sort} dir={dir} on={toggleSort} />
                      <Th label="Branch" k="branch" sort={sort} dir={dir} on={toggleSort} />
                      <th>Contact</th>
                      <Th label="Joined" k="doj" sort={sort} dir={dir} on={toggleSort} />
                      <th>Status</th>
                      <th style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.length === 0 ? (
                      <tr><td colSpan={8}>
                        <div className="empty">
                          <div className="ec"><svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg></div>
                          <h4>{loading ? "Loading employees…" : "No employees found"}</h4>
                          {!loading && <div>Try adjusting filters or search terms.</div>}
                          {!loading && <button className="ilms-btn" onClick={clearFilters} style={{ marginTop: 14 }}>Clear filters</button>}
                        </div>
                      </td></tr>
                    ) : pageRows.map((e) => (
                      <tr key={e.id}>
                        <td>
                          <div className="emp-cell">
                            <div className="rav" style={{ background: avColor(e.name) }}>{initials(e.name)}</div>
                            <div><div className="emp-nm">{e.name}</div><div className="emp-code mono">{e.code}</div></div>
                          </div>
                        </td>
                        <td>{e.designation}<div className="cell-sub"><span className="emp-type">{e.type}</span></div></td>
                        <td><span className="dept-tag"><span className="dept-dot" style={{ background: deptColor(e.department) }} />{e.department}</span></td>
                        <td>{e.branch}</td>
                        <td><div className="mono" style={{ fontSize: 12.5 }}>{e.mobile}</div><div className="cell-sub">{e.email}</div></td>
                        <td className="mono" style={{ fontSize: 12.5 }}>{fmtDate(e.doj)}</td>
                        <td><span className={"badge " + badgeCls(e.status)}><span className="bd" />{e.status}</span></td>
                        <td>
                          <div className="row-acts">
                            <button className="ico" title="Edit" onClick={() => openEdit(e.id)}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" /></svg></button>
                            <button className="ico" title="View" onClick={() => openEdit(e.id, true)}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></svg></button>
                            <button className="ico danger" title="Toggle status" onClick={() => toggleStatus(e.id)}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18.36 6.64A9 9 0 1112 3" /><path d="M12 3v9" /></svg></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="tfoot">
                <div>{filtered.length ? `Showing ${start + 1}–${Math.min(start + per, filtered.length)} of ${filtered.length}` : "Showing 0 of 0"}</div>
                <div className="pager">
                  <button className="pg" disabled={curPage === 1} onClick={() => setPage(curPage - 1)}>‹</button>
                  {pagerBtns.map((p, i) => p === "…"
                    ? <span key={"e" + i} style={{ padding: "0 4px", color: "var(--muted-2)" }}>…</span>
                    : <button key={p} className={"pg" + (p === curPage ? " on" : "")} onClick={() => setPage(p)}>{p}</button>)}
                  <button className="pg" disabled={curPage === pages} onClick={() => setPage(curPage + 1)}>›</button>
                </div>
              </div>
            </div>
          </div>
        </div>

      <Drawer
        open={drawer} editing={editing} viewOnly={viewOnly} draft={draft}
        designations={designations} departments={departments} branches={branches}
        empTypes={empTypes} managers={managers}
        onField={setF} onCombo={setF} onClose={() => setDrawer(false)} onSave={save}
      />

      {toast && (
        <div className="ilms-toast show">
          <span className="tc" style={{ background: toast.warn ? "var(--amber-2)" : "var(--ilms-teal)" }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>
          </span>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

/* ---------- Small presentational helpers ---------- */
function Th({ label, k, sort, dir, on, w }) {
  const sorted = sort === k;
  return (
    <th className={"sortable" + (sorted ? " sorted" : "")} style={w ? { minWidth: w } : undefined} onClick={() => on(k)}>
      {label} <span className="sar">{sorted ? (dir === 1 ? "↑" : "↓") : "↕"}</span>
    </th>
  );
}

function Kpi({ tone, label, value, sub, icon }) {
  return (
    <div className={"kpi " + tone}>
      <div className="lab"><span className="ic"><Ico d={icon} sz={15} /></span>{label}</div>
      <div className="val">{value}</div>
      <div className="sub">{sub}</div>
    </div>
  );
}

function Ico({ d, sz = 17 }) {
  const P = {
    grid: <><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>,
    doc: <><path d="M20 8v11a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1h8" /><path d="M14 4h6v6" /><path d="M8 12h6M8 16h4" /></>,
    box: <><path d="M4 7l8-4 8 4-8 4-8-4z" /><path d="M4 7v10l8 4 8-4V7" /><path d="M12 11v10" /></>,
    truck: <><rect x="1" y="6" width="15" height="11" rx="1.5" /><path d="M16 9h4l3 3v5h-7z" /><circle cx="6" cy="19" r="2" /><circle cx="18" cy="19" r="2" /></>,
    bill: <><path d="M4 4h16v4H4z" /><path d="M6 8v12h12V8" /><path d="M9 12h6" /></>,
    user: <><path d="M12 12a4 4 0 100-8 4 4 0 000 8z" /><path d="M4 21v-1a6 6 0 016-6h4a6 6 0 016 6v1" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    bank: <><path d="M3 21h18" /><path d="M5 21V7l7-4 7 4v14" /><path d="M9 9h.01M9 13h.01M15 9h.01M15 13h.01" /></>,
    gear: <><circle cx="12" cy="12" r="3" /><path d="M19 12a7 7 0 00-.1-1l2-1.6-2-3.4-2.4 1a7 7 0 00-1.7-1L14.5 2h-5l-.3 2.6a7 7 0 00-1.7 1l-2.4-1-2 3.4L5 11a7 7 0 000 2l-2 1.6 2 3.4 2.4-1a7 7 0 001.7 1l.3 2.6h5l.3-2.6a7 7 0 001.7-1l2.4 1 2-3.4-2-1.6c.06-.33.1-.66.1-1z" /></>,
    chart: <><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></>,
    team: <><path d="M17 20v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 20v-2a4 4 0 00-3-3.87" /></>,
    check: <><path d="M20 6L9 17l-5-5" /></>,
    dept: <><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" /></>,
  };
  return <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{P[d]}</svg>;
}